// Omnix/client/ui/WebChatWizard.js
import { ComponentBase } from "../core/ComponentBase.js";

export class WebChatWizard extends ComponentBase {
    static styles = `
        .webchat-wizard-modal .modal-dialog { max-width: 600px; }
    `;

    constructor(config) {
        super(config);
        this.modalInstance = null;
        this.configToEdit = null; // To store data for editing

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade webchat-wizard-modal';
        this.container.id = `${this.config.instanceId}-modal`;
        this.container.setAttribute('tabindex', '-1');
        this.container.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="wc-modal-title">New Web Chat Widget Setup</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <p class="text-muted">Configure the appearance and behavior of your new web chat widget.</p>
                        <div class="mb-3"><label class="form-label">Widget Name</label><input type="text" id="wc-name" class="form-control" placeholder="e.g., Main Website Chat"></div>
                        
                        <h6 class="mt-4">Routing</h6>
                        <div class="mb-3">
                            <label for="wc-workflow-select" class="form-label">Inbound Workflow</label>
                            <select id="wc-workflow-select" class="form-select">
                                <option value="">Default (Direct to Inbox)</option>
                                <!-- Workflows will be populated here -->
                            </select>
                            <div class="form-text">Select a workflow to handle incoming chats. Workflows must be tagged with 'messaging'.</div>
                        </div>

                        <h6 class="mt-4">Appearance</h6>
                        <div class="mb-2"><label class="form-label">Chat Title</label><input type="text" id="wc-title" class="form-control" value="Chat with us"></div>
                        <div class="mb-2"><label class="form-label">Welcome Message</label><input type="text" id="wc-welcome" class="form-control" value="How can we help you today?"></div>
                        <div class="row">
                            <div class="col-md-6"><label class="form-label">Primary Color</label><input type="color" id="wc-primary-color" class="form-control form-control-color" value="#0d6efd"></div>
                            <div class="col-md-6"><label class="form-label">Background Color</label><input type="color" id="wc-bg-color" class="form-control form-control-color" value="#ffffff"></div>
                        </div>
                        <h6 class="mt-4">Behavior & Security</h6>
                        <div class="mb-2"><label class="form-label">Allowed Domains</label><textarea id="wc-domains" class="form-control" rows="2" placeholder="example.com, www.another.com"></textarea><div class="form-text">Enter comma-separated domains where this widget can be embedded.</div></div>
                        <div class="form-check mt-2">
                            <input class="form-check-input" type="checkbox" id="wc-sneak-peek" checked>
                            <label class="form-check-label" for="wc-sneak-peek">Enable Agent Sneak Peek (see what users are typing)</label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="wizard-finish-btn">Create Widget</button>
                    </div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);
        this.container.querySelector('#wizard-finish-btn').addEventListener('click', this.handleFinish.bind(this));
        await super.initialize();
    }

    async show(mode = 'create', configData = null) {
        this.mode = mode;
        this.configToEdit = configData;

        const titleEl = this.container.querySelector('#wc-modal-title');
        const finishBtn = this.container.querySelector('#wizard-finish-btn');

        if (this.mode === 'edit') {
            titleEl.textContent = this.t('Edit Web Chat Widget');
            finishBtn.textContent = this.t('Save Changes');
            this.populateFormForEdit();
        } else {
            titleEl.textContent = this.t('New Web Chat Widget Setup');
            finishBtn.textContent = this.t('Create Widget');
            this.resetForm();
        }

        this.modalInstance.show();
        await this._populateWorkflows();
    }

    resetForm() {
        this.container.querySelectorAll('input, textarea, select').forEach(el => {
            if (el.type === 'checkbox') el.checked = el.id === 'wc-sneak-peek';
            else if (el.type === 'color') el.value = el.id === 'wc-primary-color' ? '#0d6efd' : '#ffffff';
            else if (el.id === 'wc-title') el.value = 'Chat with us';
            else if (el.id === 'wc-welcome') el.value = 'How can we help you today?';
            else el.value = '';
        });
    }

    populateFormForEdit() {
        if (!this.configToEdit) return;
        const settings = this.configToEdit.settings || {};
        this.container.querySelector('#wc-name').value = this.configToEdit.name || '';
        this.container.querySelector('#wc-workflow-select').value = this.configToEdit.inboundWorkflowId || '';
        this.container.querySelector('#wc-title').value = settings.title || 'Chat with us';
        this.container.querySelector('#wc-welcome').value = settings.welcomeMessage || 'How can we help you today?';
        this.container.querySelector('#wc-primary-color').value = settings.primaryColor || '#0d6efd';
        this.container.querySelector('#wc-bg-color').value = settings.backgroundColor || '#ffffff';
        this.container.querySelector('#wc-domains').value = settings.allowedDomains || '';
        this.container.querySelector('#wc-sneak-peek').checked = settings.enableTypingSneakPeek !== false;
    }


    async _populateWorkflows() {
        const select = this.container.querySelector('#wc-workflow-select');
        if (!select) return;

        try {
            const workflows = await this.appClient.rpcClient.call('ChannelManagerRpc.getFieldValues', {
                fieldName: 'inboundWorkflowId',
                reference: {
                    collection: 'workflows',
                    valueField: '_id',
                    displayField: 'name',
                    filter: { tags: 'messaging' }
                }
            });

            // Clear existing options except the default
            select.innerHTML = '<option value="">Default (Direct to Inbox)</option>';

            workflows.forEach(wf => {
                const option = document.createElement('option');
                option.value = wf.value;
                option.textContent = wf.label;
                select.appendChild(option);
            });
        } catch (error) {
            this.log.error("Failed to populate workflows in wizard:", error);
            // Optionally disable the select or show an error
        }
    }

    async handleFinish() {
        const finishBtn = this.container.querySelector('#wizard-finish-btn');
        finishBtn.disabled = true;
        const originalButtonText = finishBtn.textContent;
        finishBtn.innerHTML = `<span class="spinner-border spinner-border-sm"></span> ${this.t('Saving...')}`;

        const workflowId = this.container.querySelector('#wc-workflow-select').value;

        const config = {
            name: this.container.querySelector('#wc-name').value.trim(),
            channelType: 'web_chat',
            provider: 'native',
            isEnabled: true,
            inboundWorkflowId: workflowId || null,
            settings: {
                title: this.container.querySelector('#wc-title').value.trim(),
                welcomeMessage: this.container.querySelector('#wc-welcome').value.trim(),
                primaryColor: this.container.querySelector('#wc-primary-color').value,
                backgroundColor: this.container.querySelector('#wc-bg-color').value,
                allowedDomains: this.container.querySelector('#wc-domains').value.trim(),
                enableTypingSneakPeek: this.container.querySelector('#wc-sneak-peek').checked,
            }
        };

        if (!config.name) {
            this.appClient.notifier.error("Widget Name is required.");
            finishBtn.disabled = false;
            finishBtn.textContent = originalButtonText;
            return;
        }

        try {
            if (this.mode === 'edit') {
                config._id = this.configToEdit._id;
                await this.appClient.rpcClient.call('ChannelManagerRpc.update', config);
                this.appClient.notifier.success(`Widget '${config.name}' updated successfully!`);
            } else {
                await this.appClient.rpcClient.call('ChannelManagerRpc.create', config);
                this.appClient.notifier.success(`Web Chat widget '${config.name}' created successfully!`);
            }
            this.emit('channelSaved');
            this.modalInstance.hide();
        } catch (error) {
            this.log.error(`Failed to ${this.mode} Web Chat widget:`, error);
            this.appClient.notifier.error(`${this.capitalizeFirstLetter(this.mode)} failed: ${error.message}`);
        } finally {
            finishBtn.disabled = false;
            finishBtn.textContent = originalButtonText;
        }
    }

    capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }


    render() { return this.container; }
    destroy() { this.modalInstance?.dispose(); this.container?.remove(); super.destroy(); }
}